import { DocFieldState } from "../types";
import { DriverSection, SectionField } from "../../redux/onBoardingSlice.types";
import { DOC_STATE } from "./constants";
import { RejectionPayload, SectionTrackingState } from "./types";

/**
 * Builds the rejection payload from current review state.
 *
 * Pure function — takes a snapshot of state and returns data.
 * Easily unit-testable without any React context.
 *
 * Rules:
 * - Every field key present in a section's `rejectedFields` map is included with its own reason.
 * - Individual doc rejections are included only for fields not already covered by a section rejection.
 */
export const buildRejectionPayload = (
  sortedSections: DriverSection[],
  sectionStates: Record<string, SectionTrackingState>,
  allImageFields: Pick<SectionField, "fieldId" | "key">[],
  docStates: Record<string, DocFieldState>
): RejectionPayload => {
  const rejectedFields: { field: string; rejectionReason: string }[] = [];
  const sectionRejectedKeys = new Set<string>();

  sortedSections.forEach((section) => {
    const sState = sectionStates[section.sectionId];
    if (!sState) return;
    Object.entries(sState.rejectedFields).forEach(([key, reason]) => {
      rejectedFields.push({ field: key, rejectionReason: reason });
      sectionRejectedKeys.add(key);
    });
  });

  allImageFields.forEach((field) => {
    if (field.key && sectionRejectedKeys.has(field.key)) return;
    const dState = docStates[field.fieldId];
    if (dState?.state === DOC_STATE.REJECTED && dState.reasons.length > 0 && field.key) {
      rejectedFields.push({ field: field.key, rejectionReason: dState.reasons.join(", ") });
    }
  });

  return { rejectedFields };
};
